'use client';

import { useRef, useState, useEffect } from 'react';
import { EditorContextProvider, useFileBrowser } from '@pyranger/editor';
import type { EditorRefContext } from '@pyranger/types';
import {
    DEFAULT_SETTINGS,
    INITIAL_REF_CONTEXT,
    INITIAL_TEXT_EDITOR_CONTEXT,
} from '@pyranger/constants';
import { DeviceMutex, TerminalFormatter } from '@pyranger/editor';
import Link from 'next/link';

import { useCustomAlert } from './hooks';

export default function EditorRouteLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const { showCustomAlert } = useCustomAlert();
    const fileBrowserUtils = useFileBrowser();
    const [supportsWebSerial, setSupportsWebSerial] = useState<
        boolean | undefined
    >();
    const ref = useRef<EditorRefContext>({
        ...INITIAL_REF_CONTEXT,
        device: {
            mutex: new DeviceMutex(),
            activeTransaction: null,
            terminalFormatter: new TerminalFormatter(),
            debugInBuffer: '',
            debugInFlushScheduled: false,
            debugOutBuffer: '',
            debugOutFlushScheduled: false,
        },
    });
    const dataInRef = useRef<HTMLTextAreaElement>(null);
    const dataOutRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (typeof navigator !== 'undefined') {
            if ('serial' in navigator) {
                setSupportsWebSerial(true);
            } else {
                setSupportsWebSerial(false);
            }
        }
    }, []);

    if (supportsWebSerial === undefined) {
        return null;
    }

    if (supportsWebSerial === false) {
        return (
            <div className="m-8 grid place-content-center self-center">
                <div className="m-10 grid max-w-[76ch] gap-4">
                    <h1 className="text-2xl">Unsupported browser detected</h1>
                    <p>
                        To use the PyRanger editor, you must be using a web
                        browser that supports the Web Serial API. The complete
                        list of supported browsers is available{' '}
                        <a
                            href="https://caniuse.com/web-serial"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here
                        </a>
                        .
                    </p>
                    <Link href="/">Go back</Link>
                </div>
            </div>
        );
    }

    return (
        <EditorContextProvider
            options={{
                input: {
                    context: {
                        dataInRef,
                        dataOutRef,
                        deviceLabel: null,
                        fileBrowser: {
                            ...fileBrowserUtils,
                            lastActiveFolder: null,
                        },
                        ref,
                        settings: DEFAULT_SETTINGS,
                        showCustomAlert,
                        syncStatus: new Map(),
                        textEditor: INITIAL_TEXT_EDITOR_CONTEXT,
                    },
                },
            }}
        >
            {children}
        </EditorContextProvider>
    );
}
